import { apiGet, apiPost, apiPatch, apiDelete, extractIdFromLink } from './api';
import type {
  WorkPackage,
  HalCollection,
  WorkPackageSchema,
  Relation,
  WorkPackageFormData,
} from '@/types';

// Obținere work packages pentru un proiect
export async function getProjectWorkPackages(
  projectId: number,
  filters?: {
    typeId?: number;
    statusId?: number;
    assigneeId?: number | string;
  }
): Promise<WorkPackage[]> {
  const params: Record<string, unknown> = {
    pageSize: 200,
  };

  // Construim filtrele în formatul OpenProject
  const filterArray: string[] = [];

  if (filters?.typeId) {
    filterArray.push(JSON.stringify({
      type_id: { operator: '=', values: [String(filters.typeId)] },
    }));
  }

  if (filters?.statusId) {
    filterArray.push(JSON.stringify({
      status_id: { operator: '=', values: [String(filters.statusId)] },
    }));
  }

  if (filters?.assigneeId) {
    const value = filters.assigneeId === 'me' ? 'me' : String(filters.assigneeId);
    filterArray.push(JSON.stringify({
      assignee: { operator: '=', values: [value] },
    }));
  }

  if (filterArray.length > 0) {
    params.filters = `[${filterArray.join(',')}]`;
  }

  const response = await apiGet<HalCollection<WorkPackage>>(
    `/projects/${projectId}/work_packages`,
    { params }
  );

  return response._embedded.elements;
}

// Obținere activitățile mele (work packages unde sunt assignee)
export async function getMyWorkPackages(filters?: {
  projectId?: number;
  typeId?: number;
  statusId?: number;
  dueDateRange?: 'today' | 'week' | 'month' | 'all';
}): Promise<WorkPackage[]> {
  const params: Record<string, unknown> = {
    pageSize: 200,
  };

  // Filtru pentru assignee = utilizator curent
  const filterArray: { [key: string]: { operator: string; values: string[] } }[] = [
    { assignee: { operator: '=', values: ['me'] } },
  ];

  if (filters?.projectId) {
    filterArray.push({
      project_id: { operator: '=', values: [String(filters.projectId)] },
    });
  }

  if (filters?.typeId) {
    filterArray.push({
      type_id: { operator: '=', values: [String(filters.typeId)] },
    });
  }

  if (filters?.statusId) {
    filterArray.push({
      status_id: { operator: '=', values: [String(filters.statusId)] },
    });
  }

  // Filtru după dată
  if (filters?.dueDateRange && filters.dueDateRange !== 'all') {
    const today = new Date();
    let endDate: Date;

    switch (filters.dueDateRange) {
      case 'today':
        endDate = today;
        break;
      case 'week':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() + 7);
        break;
      case 'month':
        endDate = new Date(today);
        endDate.setMonth(endDate.getMonth() + 1);
        break;
    }

    const formatDate = (d: Date) => d.toISOString().split('T')[0];
    filterArray.push({
      dueDate: {
        operator: '<>d',
        values: [formatDate(today), formatDate(endDate)],
      },
    });
  }

  params.filters = JSON.stringify(filterArray);

  const response = await apiGet<HalCollection<WorkPackage>>(
    '/work_packages',
    { params }
  );

  return response._embedded.elements;
}

// Obținere work package după ID
export async function getWorkPackage(workPackageId: number): Promise<WorkPackage> {
  return apiGet<WorkPackage>(`/work_packages/${workPackageId}`);
}

// Obținere schema work package pentru un proiect
export async function getWorkPackageSchema(
  projectId: number,
  typeId?: number
): Promise<WorkPackageSchema> {
  let endpoint = `/projects/${projectId}/work_packages/form`;

  if (typeId) {
    const formResponse = await apiPost<{
      _embedded: { schema: WorkPackageSchema };
    }>(endpoint, {
      _links: {
        type: { href: `/api/v3/types/${typeId}` },
      },
    });
    return formResponse._embedded.schema;
  }

  const formResponse = await apiPost<{
    _embedded: { schema: WorkPackageSchema };
  }>(endpoint, {});
  return formResponse._embedded.schema;
}

// Creare work package
export async function createWorkPackage(
  projectId: number,
  data: WorkPackageFormData
): Promise<WorkPackage> {
  const payload: Record<string, unknown> = {
    subject: data.subject,
  };

  if (data.description) {
    payload.description = { raw: data.description };
  }

  if (data.startDate) {
    payload.startDate = data.startDate;
  }

  if (data.dueDate) {
    payload.dueDate = data.dueDate;
  }

  if (data.estimatedTime) {
    payload.estimatedTime = data.estimatedTime;
  }

  if (data.remainingTime) {
    payload.remainingTime = data.remainingTime;
  }

  // Links pentru relații
  const links: Record<string, unknown> = {};

  if (data.typeId) {
    links.type = { href: `/api/v3/types/${data.typeId}` };
  }

  if (data.statusId) {
    links.status = { href: `/api/v3/statuses/${data.statusId}` };
  }

  if (data.priorityId) {
    links.priority = { href: `/api/v3/priorities/${data.priorityId}` };
  }

  if (data.assigneeId) {
    links.assignee = { href: `/api/v3/users/${data.assigneeId}` };
  }

  if (data.responsibleId) {
    links.responsible = { href: `/api/v3/users/${data.responsibleId}` };
  }

  if (data.parentId) {
    links.parent = { href: `/api/v3/work_packages/${data.parentId}` };
  }

  if (data.versionId) {
    links.version = { href: `/api/v3/versions/${data.versionId}` };
  }

  if (data.categoryId) {
    links.category = { href: `/api/v3/categories/${data.categoryId}` };
  }

  if (Object.keys(links).length > 0) {
    payload._links = links;
  }

  // Câmpuri custom
  if (data.customFields) {
    Object.assign(payload, data.customFields);
  }

  return apiPost<WorkPackage>(
    `/projects/${projectId}/work_packages`,
    payload
  );
}

// Actualizare work package
export async function updateWorkPackage(
  workPackageId: number,
  lockVersion: number,
  data: Partial<WorkPackageFormData>
): Promise<WorkPackage> {
  const payload: Record<string, unknown> = {
    lockVersion,
  };

  if (data.subject !== undefined) {
    payload.subject = data.subject;
  }

  if (data.description !== undefined) {
    payload.description = data.description ? { raw: data.description } : null;
  }

  if (data.startDate !== undefined) {
    payload.startDate = data.startDate || null;
  }

  if (data.dueDate !== undefined) {
    payload.dueDate = data.dueDate || null;
  }

  if (data.estimatedTime !== undefined) {
    payload.estimatedTime = data.estimatedTime || null;
  }

  if (data.remainingTime !== undefined) {
    payload.remainingTime = data.remainingTime || null;
  }

  // Links pentru relații
  const links: Record<string, unknown> = {};

  if (data.typeId !== undefined) {
    links.type = data.typeId
      ? { href: `/api/v3/types/${data.typeId}` }
      : null;
  }

  if (data.statusId !== undefined) {
    links.status = data.statusId
      ? { href: `/api/v3/statuses/${data.statusId}` }
      : null;
  }

  if (data.priorityId !== undefined) {
    links.priority = data.priorityId
      ? { href: `/api/v3/priorities/${data.priorityId}` }
      : null;
  }

  if (data.assigneeId !== undefined) {
    links.assignee = data.assigneeId
      ? { href: `/api/v3/users/${data.assigneeId}` }
      : null;
  }

  if (data.responsibleId !== undefined) {
    links.responsible = data.responsibleId
      ? { href: `/api/v3/users/${data.responsibleId}` }
      : null;
  }

  if (data.parentId !== undefined) {
    links.parent = data.parentId
      ? { href: `/api/v3/work_packages/${data.parentId}` }
      : null;
  }

  if (data.versionId !== undefined) {
    links.version = data.versionId
      ? { href: `/api/v3/versions/${data.versionId}` }
      : null;
  }

  if (data.categoryId !== undefined) {
    links.category = data.categoryId
      ? { href: `/api/v3/categories/${data.categoryId}` }
      : null;
  }

  if (Object.keys(links).length > 0) {
    payload._links = links;
  }

  // Câmpuri custom
  if (data.customFields) {
    Object.assign(payload, data.customFields);
  }

  return apiPatch<WorkPackage>(`/work_packages/${workPackageId}`, payload);
}

// Ștergere work package
export async function deleteWorkPackage(workPackageId: number, reason?: string): Promise<void> {
  await apiDelete(`/auth/data/work-packages/${workPackageId}`, {
    data: reason ? { reason } : undefined
  });
}

// Obținere relații pentru un work package
export async function getWorkPackageRelations(
  workPackageId: number
): Promise<Relation[]> {
  const response = await apiGet<HalCollection<Relation>>(
    `/work_packages/${workPackageId}/relations`
  );

  return response._embedded.elements;
}

// Creare relație între work packages
export async function createRelation(
  fromId: number,
  toId: number,
  type: string,
  delay?: number
): Promise<Relation> {
  return apiPost<Relation>('/relations', {
    _links: {
      from: { href: `/api/v3/work_packages/${fromId}` },
      to: { href: `/api/v3/work_packages/${toId}` },
    },
    type,
    delay,
  });
}

// Ștergere relație
export async function deleteRelation(relationId: number): Promise<void> {
  await apiDelete(`/relations/${relationId}`);
}

// Obținere copii (sub-work packages)
export async function getWorkPackageChildren(
  workPackageId: number
): Promise<WorkPackage[]> {
  const response = await apiGet<HalCollection<WorkPackage>>(
    '/work_packages',
    {
      params: {
        filters: JSON.stringify([
          { parent: { operator: '=', values: [String(workPackageId)] } },
        ]),
        pageSize: 100,
      },
    }
  );

  return response._embedded.elements;
}

// Helper pentru extragere ID din link
export function getWorkPackageIdFromLink(wp: WorkPackage): number {
  return wp.id || extractIdFromLink(wp._links.self) || 0;
}

// Helper pentru extragere type ID
export function getTypeIdFromLink(wp: WorkPackage): number | null {
  return extractIdFromLink(wp._links.type);
}

// Helper pentru extragere status ID
export function getStatusIdFromLink(wp: WorkPackage): number | null {
  return extractIdFromLink(wp._links.status);
}

// Helper pentru extragere priority ID
export function getPriorityIdFromLink(wp: WorkPackage): number | null {
  return extractIdFromLink(wp._links.priority);
}

// Helper pentru extragere assignee ID
export function getAssigneeIdFromLink(wp: WorkPackage): number | null {
  return extractIdFromLink(wp._links.assignee);
}

// Helper pentru extragere parent ID
export function getParentIdFromLink(wp: WorkPackage): number | null {
  return extractIdFromLink(wp._links.parent);
}

// Helper pentru extragere project ID
export function getProjectIdFromLink(wp: WorkPackage): number | null {
  return extractIdFromLink(wp._links.project);
}
